/**
 * Services Index
 * Central export point for all service classes and utilities
 */

// Core services
export { apiClient } from './core/apiClient';
export { graphqlClient } from './core/graphqlClient';
export { serviceFactory, AbstractService } from './core/serviceFactory';
export type { ServiceContext, BaseService } from './core/serviceFactory';
export type { ApiResponse, ApiRequestOptions } from './core/apiClient';
export type { GraphQLClientResponse, GraphQLRequest } from './core/graphqlClient';

// EA (VPE Admin) services
export { EAVPEManagementService } from './ea/vpeManagementService';
export type { VPE, CreateVPEData, UpdateVPEData } from './features/VPEManagementService';

export { enrollerAdminService, EnrollerAdminService } from './ea/enrollerAdminService';
export type { 
  MetabaseTokenResponse, 
  MetabaseConfig, 
  MetabaseResource,
  MetabaseResourcesResponse 
} from './ea/enrollerAdminService';

// Feature services
export { EnrollmentSpecialistsService } from './features/EnrollmentSpecialistsService';
export type { EnrollmentSpecialist, EnrollmentSpecialistsResponse } from './features/EnrollmentSpecialistsService';

export { GroupFilteringService } from './features/GroupFilteringService';
export type { FilterOption as GroupFilterOption, FilterParams } from './features/GroupFilteringService';

export { phoneValidationService } from './features/PhoneValidationService';
export { PhoneValidationService } from './features/PhoneValidationService';

export { appointmentService } from './features/AppointmentService';
export { AppointmentService } from './features/AppointmentService';

export { esPatientTransferService, ESPatientTransferService } from './features/ESPatientTransferService';
export type { ESTransferPayload, ESTransferResponse } from './features/ESPatientTransferService';

// Import dependencies for convenience methods
import { serviceFactory } from './core/serviceFactory';
import { EAVPEManagementService } from './ea/vpeManagementService';
import { EnrollmentSpecialistsService } from './features/EnrollmentSpecialistsService';
import { GroupFilteringService } from './features/GroupFilteringService';
import { GroupsService } from './features/GroupsService';

// Service factory convenience methods
export const getGroupsService = () => {
  return serviceFactory.getService(GroupsService);
};

export const getEnrollmentSpecialistsService = () => {
  return serviceFactory.getService(EnrollmentSpecialistsService);
};

export const getEAVPEManagementService = () => {
  return new EAVPEManagementService();
};

export const getGroupFilteringService = () => {
  return serviceFactory.getService(GroupFilteringService);
};

// Initialize services with auth context (to be called from AuthContext)
export const initializeServices = (context: {
  userId?: string | number;
  hasuraToken?: string;
  userType?: string;
}) => {
  serviceFactory.setGlobalContext(context);
};

// Update service context (useful for token refresh)
export const updateServiceContext = (context: Partial<{
  userId?: string | number;
  hasuraToken?: string;
  userType?: string;
}>) => {
  serviceFactory.updateGlobalContext(context);
};